/* eslint-disable @typescript-eslint/no-unused-vars */
import { call, takeLatest } from 'redux-saga/effects';
import { User, UserRequest } from '@dynamika-shared/types/users';
import { toast } from 'react-toastify';
import * as api from '@api/users';
import axios from 'axios';
import { actions } from './usersList.slice';
import {
  UsersListDoUserAction,
  UsersListFetchAction,
  ActionName,
} from './usersList.types';
import { Resp, dispatch } from '../helpers';
import { runToast } from '../tools';

export const apiErrorMessage = (actionName: ActionName) =>
  runToast(
    'error',
    `There is no "userRequest", "userId" or "user" parameter in ${actionName}`
  );

export const messages = {
  deactivateUser: {
    success: (firstName: string, lastName: string) =>
      `User deactivated. ${firstName} ${lastName} does not have access to the Dynamika platform`,
    fail: 'Failed to deactivate user',
  },
  reactivateUser: {
    success: (firstName: string, lastName: string) =>
      `User activated. ${firstName} ${lastName} has access to the Dynamika platform`,
    fail: 'Failed to activate user',
  },
  resendInvitation: {
    success: 'Invite sent',
    fail: 'Failed to resend invitation',
  },
  createUser: {
    success: 'User created successfully',
    fail: {
      email: (email: string) =>
        `Email address ${email} already exists in the system. Please enter a different email.`,
      default: 'Failed to create user',
    },
  },
  updateUser: {
    success: 'User updated successfully',
    fail: 'Failed to update user',
  },
};

export function* deactivateUserWorker(
  user: UsersListDoUserAction['payload']['user']
) {
  if (!user) {
    yield call(apiErrorMessage, 'deactivateUser');
    return;
  }
  try {
    yield call(api.deactivateUser, user.id);
    yield call(
      runToast,
      'success',
      messages.deactivateUser.success(user.firstName, user.lastName)
    );
  } catch (error) {
    yield call(runToast, 'error', messages.deactivateUser.fail);
    throw error;
  }
}

export function* reactivateUserWorker(
  user: UsersListDoUserAction['payload']['user']
) {
  if (!user) {
    yield call(apiErrorMessage, 'reactivateUser');
    return;
  }
  try {
    yield call(api.reactivateUser, user.id);
    yield call(
      runToast,
      'success',
      messages.reactivateUser.success(user.firstName, user.lastName)
    );
  } catch (error) {
    yield call(runToast, 'error', messages.reactivateUser.fail);
    throw error;
  }
}

export function* resendInvitationWorker(
  user: UsersListDoUserAction['payload']['user']
) {
  if (!user) {
    yield call(apiErrorMessage, 'resendInvitation');
    return;
  }
  try {
    yield call(api.resendInvitation, user.id);
    yield call(runToast, 'success', messages.resendInvitation.success);
  } catch (error) {
    yield call(runToast, 'error', messages.resendInvitation.fail);
    throw error;
  }
}

export function* createUserWorker(
  userRequest: UsersListDoUserAction['payload']['userRequest']
) {
  if (!userRequest) {
    yield call(apiErrorMessage, 'createUser');
    return;
  }
  try {
    yield call(api.createUser, userRequest);
    yield call(runToast, 'success', messages.createUser.success);
  } catch (error) {
    if (
      axios.isAxiosError(error) &&
      [409].includes(Number(error.response?.status))
    ) {
      yield call(
        runToast,
        'error',
        error.response?.data.detail ||
          messages.createUser.fail.email(userRequest.email)
      );
    } else {
      yield call(runToast, 'error', messages.createUser.fail.default);
    }
    throw error;
  }
}

export function* updateUserWorker(
  userRequest: UsersListDoUserAction['payload']['userRequest'],
  userId: UsersListDoUserAction['payload']['userId']
) {
  if (!(userId && userRequest)) {
    yield call(apiErrorMessage, 'updateUser');
    return;
  }
  try {
    yield call(api.updateUser, userRequest, userId);
    yield call(runToast, 'success', messages.updateUser.success);
  } catch (error) {
    if (
      axios.isAxiosError(error) &&
      [409].includes(Number(error.response?.status))
    ) {
      yield call(runToast, 'error', error.response?.data.detail);
    } else {
      yield call(runToast, 'error', messages.updateUser.fail);
    }
    throw error;
  }
}

export function* callUserAPI(
  actionName: ActionName,
  user?: User,
  userRequest?: UserRequest,
  userId?: string
): Generator<unknown, Promise<unknown>, Promise<unknown>> {
  if (actionName === 'deactivateUser') {
    return yield call(deactivateUserWorker, user);
  }
  if (actionName === 'reactivateUser') {
    return yield call(reactivateUserWorker, user);
  }

  if (actionName === 'resendInvitation') {
    return yield call(resendInvitationWorker, user);
  }

  if (actionName === 'createUser') {
    return yield call(createUserWorker, userRequest);
  }
  if (actionName === 'updateUser') {
    return yield call(updateUserWorker, userRequest, userId);
  }

  return yield call(apiErrorMessage, actionName);
}

export function* fetchUsersListWorker(action: UsersListFetchAction) {
  try {
    const response: Resp<typeof api.fetchUsersList> = yield call(
      api.fetchUsersList,
      action.payload
    );
    yield dispatch(actions.fetchUsersListSuccess(response.data));
  } catch (error) {
    yield dispatch(actions.fetchUsersListFailure(error as Error));
  }
}

export function* fetchUsersInstitutionsWorker() {
  try {
    const response: Resp<typeof api.fetchUsersInstitution> = yield call(
      api.fetchUsersInstitution
    );
    yield dispatch(actions.fetchUsersInstitutionSuccess(response.data));
  } catch (error) {
    yield dispatch(actions.fetchUsersInstitutionFailure(error as Error));
  }
}

export function* doUserWorker(action: UsersListDoUserAction) {
  const { actionName, user, userId, userRequest } = action.payload;

  try {
    yield call(callUserAPI, actionName, user, userRequest, userId);
    yield dispatch(actions.doUserSuccess({ actionName }));
  } catch (error) {
    yield dispatch(
      actions.doUserFailure({ actionName, error: error as Error })
    );
  }
}

export function* usersListSaga() {
  yield takeLatest(actions.fetchUsersList.type, fetchUsersListWorker);
  yield takeLatest(
    actions.fetchUsersInstitution.type,
    fetchUsersInstitutionsWorker
  );
  yield takeLatest(actions.doUser.type, doUserWorker);
}
